<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, reactive } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import { chargebeeComponents } from '@shared/modules/chargebee';
    import { ApiError, BillingAddress, SmsTopUpOffer, SmsTopUpRequest, confirmSmsTopUpCard, startSmsTopUpCard } from './AccountPrePaidBalance.access';
    import { billingAddressText } from './SmsTopUp.helpers';
    import SmsTopUpTotal from './SmsTopUpTotal.vue';

    // The own card step of the SMS top-up (card 5.3), after the parent's address step: the billing address (complete), the total
    // incl. VAT, Chargebee's Payment Component (card only, 3DS inside), then the confirm that creates the paid invoice. A declined
    // card, a failed 3DS challenge or a closed step charges nothing (Validation §36.5). A failed call (a refusal or not) goes to
    // the parent as it is: the parent shows it — except a confirm whose outcome is unknown (`unconfirmed`).
    const props = defineProps<{ offer: SmsTopUpOffer; request: SmsTopUpRequest; address: BillingAddress }>();
    const emit = defineEmits<{
        // The card was accepted and the confirm has started: the step must not be closed until it answers.
        (e: 'confirming'): void;
        (e: 'paid'): void;
        (e: 'failed', error: ApiError | undefined): void;
        // The confirm failed in a way that may have come after the paid invoice was created (Validation §37.1, S3).
        (e: 'unconfirmed'): void;
        (e: 'retry'): void;
    }>();

    const state = reactive<{
        step: 'loading' | 'paying' | 'confirming' | 'declined';
        net: number | null;
        total: number | null;
        currency: string;
    }>({
        step: 'loading',
        net: null,
        total: null,
        currency: '',
    });

    const addressText = computed(() => billingAddressText(props.address));

    const fail = (error: ApiError | undefined) => emit('failed', error);

    onMounted(async () => {
        try {
            await start();
        } catch (error) {
            // A 409 from the start, or Chargebee.js not loading (blocked, CDN down): nothing was charged.
            fail(error as ApiError);
        }
    });

    // Chargebee's payment component and button for this step's intent. Closed when the step goes away (a payment done, the modal
    // closed, a retry remounting the step): an unclosed one stays alive and the next step's never loads.
    type ChargebeeComponent = { mount(target: string): Promise<boolean>; close(): void };
    let paymentComponent: ChargebeeComponent | null = null;
    let paymentButton: ChargebeeComponent | null = null;
    let unmounted = false;

    function closeChargebeeComponents(): void {
        for (const component of [paymentComponent, paymentButton]) {
            try {
                component?.close();
            } catch {
                // Already gone with the DOM: nothing left to close.
            }
        }
        paymentComponent = null;
        paymentButton = null;
    }

    onBeforeUnmount(() => {
        unmounted = true;
        closeChargebeeComponents();
    });

    async function start(): Promise<void> {
        const started = await startSmsTopUpCard(props.request);
        const components = await chargebeeComponents(props.offer.Site!, props.offer.PublishableKey);
        // Closed while the intent was being created: mount nothing.
        if (unmounted) return;
        state.net = started.NetInMinorUnits;
        // What the card is charged: the intent's amount (the server makes it the invoice's total, Validation §36 T5).
        state.total = started.PaymentIntent.amount;
        state.currency = started.PaymentIntent.currency_code;
        state.step = 'paying';
        await nextTick();
        if (unmounted) return;

        const payment: ChargebeeComponent = components.create(
            'payment',
            {
                paymentIntent: started.PaymentIntent,
                layout: { type: 'tab', showRadioButtons: false },
                paymentMethods: { allowed: ['card'], sortOrder: ['card'] },
            },
            {
                onSuccess: (paymentIntent: { id: string }) => confirm(paymentIntent.id),
                // Declined, 3DS failed or abandoned: nothing was charged. Stripe's own text is English — ours is shown.
                onError: () => {
                    state.step = 'declined';
                },
                // Required by the component; nothing of ours to check before the card is submitted.
                onButtonClick: () => Promise.resolve(),
            }
        );
        // The button's errors are the payment's: the component's onError above already shows them.
        const button: ChargebeeComponent = components.create('payment-button', {}, { onError: () => {} });
        paymentComponent = payment;
        paymentButton = button;
        await payment.mount('#sms-top-up-payment-component');
        await button.mount('#sms-top-up-payment-button');
    }

    async function confirm(paymentIntentId: string): Promise<void> {
        // Closed before Chargebee answered (e.g. during a 3DS challenge): a closed step charges nothing — the authorized intent
        // is never consumed and its reservation is released when it expires (Validation §36.5). Confirming here would charge the
        // card with no step left to show it.
        if (unmounted) return;
        state.step = 'confirming';
        emit('confirming');
        try {
            await confirmSmsTopUpCard(props.request, paymentIntentId);
            emit('paid');
        } catch (error) {
            const apiError = error as ApiError;
            // A 409 (refused) or 502 (Chargebee refused the invoice) charged nothing: the reservation is released when the intent
            // expires within the hour (Validation §36.5). Anything else — a 500, a 503, a lost response — may have come after the
            // paid invoice was created, so the parent waits for the funds instead of saying it failed (Validation §37.1, S3).
            if (apiError?.status === 409 || apiError?.status === 502) fail(apiError);
            else emit('unconfirmed');
        }
    }
</script>

<template>
    <Loading
        :show="state.step === 'loading' || state.step === 'confirming'"
        :labelSrOnly="false"
        :label="state.step === 'confirming' ? $Resources.SmsTopUpConfirmingPaymentLabel : $Resources.SmsTopUpPreparingPaymentLabel"></Loading>

    <!-- Hidden, not removed, while confirming: the component is closed only when the step goes away (onBeforeUnmount). -->
    <section v-show="state.step === 'paying'">
        <p class="c-light">{{ $Resources.SmsTopUpBillingAddressHeading }}: {{ addressText }}</p>
        <SmsTopUpTotal v-if="state.net != null && state.total != null" :net="state.net" :total="state.total" :currencyCode="state.currency" />
        <div id="sms-top-up-payment-component"></div>
        <div id="sms-top-up-payment-button"></div>
    </section>

    <section v-if="state.step === 'declined'">
        <BsAlert :type="AlertTypes.Warning" :dismissible="false" :content="$Resources.SmsTopUpCardDeclinedMessage" />
        <div class="text-right">
            <button class="btn btn-primary" @click="emit('retry')">{{ $Resources.SmsTopUpTryAgainBtn }}</button>
        </div>
    </section>
</template>
